import React from 'react';
import { BLOCK_TYPE, SIZE, TILE_COLORS } from '../game/constants';
import { coordsKey, tileClassName } from '../game/tiles';

const BoardGrid = ({
  tiles,
  sizeVars,
  className = '',
  overlay = null,
  style = {},
  muted = false,
  pointerEvents = 'auto',
  colorMap = null,
  onCellClick,
  onCellHover,
  dimOthers = false,
  highlightCells = [],
  ...handlers
}) => {
  const now = Date.now();
  const tileMap = new Map();
  tiles.forEach((tile) => tileMap.set(coordsKey(tile.row, tile.col), tile));
  const highlightSet = new Set(
    (highlightCells || [])
      .filter(Boolean)
      .map((cell) => `${cell.row}-${cell.col}`)
  );
  const basePalette = Object.keys(TILE_COLORS).map(Number);
  const palette = colorMap?.palette?.length ? colorMap.palette : basePalette;
  const offset = colorMap?.offset ? colorMap.offset : 0;
  const valueUseCount = new Map();

  return (
    <div
      className={`relative ${className}`}
      style={{
        ...sizeVars,
        touchAction: 'none',
        pointerEvents,
        ...(muted ? { filter: 'grayscale(0.25)', opacity: 0.95 } : {}),
        ...style,
      }}
      onMouseLeave={onCellHover ? () => onCellHover(null) : undefined}
      {...handlers}
    >
      <div className="absolute inset-0 grid grid-cols-4" style={{ gap: 'var(--tile-gap)' }}>
        {Array.from({ length: SIZE * SIZE }).map((_, idx) => {
          const r = Math.floor(idx / SIZE);
          const c = idx % SIZE;
          const key = coordsKey(r, c);
          const occupied = tileMap.has(key);
          const isHighlightCell = highlightSet.has(`${r}-${c}`);
          return (
            <div
              key={key}
              className={`rounded-[22px] border-[6px] border-black bg-[#8f8f8f] transition-transform ${
                onCellClick ? 'cursor-pointer hover:scale-[1.01] hover:border-[#111]' : ''
              }`}
              style={{
                width: 'var(--tile-size)',
                height: 'var(--tile-size)',
                ...(dimOthers && !isHighlightCell ? { filter: 'grayscale(0.25) brightness(0.78)' } : {}),
              }}
              onClick={
                onCellClick
                  ? () => {
                      onCellClick(r, c, occupied);
                    }
                  : undefined
              }
              onMouseEnter={onCellHover ? () => onCellHover(r, c, occupied) : undefined}
            />
          );
        })}
      </div>
      {tiles.map((tile) => {
        const x = `calc(${tile.col} * (var(--tile-size) + var(--tile-gap)))`;
        const y = `calc(${tile.row} * (var(--tile-size) + var(--tile-gap)))`;
        const remaining =
          tile.type === BLOCK_TYPE
            ? Math.min(20, Math.max(0, Math.ceil(((tile.expiresAt || now + 20000) - now) / 1000)))
            : null;
        const isHighlight = highlightSet.has(`${tile.row}-${tile.col}`);
        let colorValue = null;
        if (colorMap && tile.type !== BLOCK_TYPE && typeof tile.value === 'number') {
          if (colorMap.palette) {
            const useIdx = valueUseCount.get(tile.value) || 0;
            colorValue = palette[(offset + useIdx) % palette.length] || null;
            valueUseCount.set(tile.value, useIdx + 1);
          } else {
            colorValue = colorMap[tile.value] || null;
          }
        }
        const tileForClass = colorValue ? { ...tile, colorValue } : tile;
        return (
          <div
            key={tile.id}
            className={tileClassName(tileForClass)}
            style={{
              width: 'var(--tile-size)',
              height: 'var(--tile-size)',
              '--tile-x': x,
              '--tile-y': y,
              transform: 'translate(var(--tile-x), var(--tile-y))',
              ...(dimOthers
                ? {
                    filter: isHighlight ? 'none' : 'grayscale(0.4) brightness(0.7)',
                    zIndex: isHighlight ? 2 : 1,
                  }
                : {}),
              pointerEvents: onCellClick ? 'none' : 'auto',
            }}
          >
            {tile.type === BLOCK_TYPE ? remaining : tile.value}
          </div>
        );
      })}
      {overlay}
    </div>
  );
};

export default BoardGrid;
